import React, { memo, useMemo, useCallback, useState } from 'react';
import { extractAllSvgFromContent } from './contentPreprocessor';
import { SvgPickerModal } from './SvgPickerModal';
import '../../styles/designButton.css';

export interface DesignButtonProps {
  /** 消息文本内容 */
  readonly content: string;
  /** 点击设计回调，传出经过安全清洗的 SVG 代码 */
  readonly onDesign: (svgContent: string) => void;
}

/**
 * 设计按钮（放在 message-footer）
 *
 * 行为：
 * - 自动检测消息内容中是否包含 SVG，无则不渲染
 * - 只有 1 个 SVG → 直接回调
 * - 多个 SVG → 弹出选择弹窗
 */
export const DesignButton: React.FC<DesignButtonProps> = memo(({ content, onDesign }) => {
  const [pickerOpen, setPickerOpen] = useState(false);

  const svgList = useMemo(() => extractAllSvgFromContent(content), [content]);

  const handleClick = useCallback((): void => {
    if (svgList.length === 0) return;

    if (svgList.length === 1) {
      onDesign(svgList[0]);
    } else {
      setPickerOpen(true);
    }
  }, [svgList, onDesign]);

  const handlePick = useCallback((svg: string): void => {
    setPickerOpen(false);
    onDesign(svg);
  }, [onDesign]);

  const handleClosePicker = useCallback((): void => {
    setPickerOpen(false);
  }, []);

  // 没有 SVG 则不渲染
  if (svgList.length === 0) return null;

  return (
    <>
      <button
        type="button"
        className="msg-design-btn"
        onClick={handleClick}
        title="将 SVG 应用到设计画布"
      >
        <svg className="msg-design-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
        <span>设计</span>
        {svgList.length > 1 && (
          <span className="msg-design-badge">{svgList.length}</span>
        )}
      </button>

      {pickerOpen && (
        <SvgPickerModal
          svgList={svgList}
          onPick={handlePick}
          onClose={handleClosePicker}
        />
      )}
    </>
  );
});

DesignButton.displayName = 'DesignButton';